import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, InputLabel,
  MenuItem, Select, Stack, TextField, Typography
} from '@mui/material';
import { showmodal, ajout, addOne } from './versementsSlice';
import { imprimerBonVersement } from './imprimerBonVersement';

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const money = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseMoney = (v) => Number(String(v || '0').replace(',', '.'));

export default function Ajouter({ initialDate, onSaved }) {
  const dispatch = useDispatch();
  const userData = useSelector(state => state.userData);
  const { modalShow, listVendeurs } = useSelector(state => state.versementsData);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [date, setDate] = useState(initialDate || localToday());
  const [vendeurId, setVendeurId] = useState('');
  const [espece, setEspece] = useState('');
  const [cheque, setCheque] = useState('');
  const [banque, setBanque] = useState('');
  const [raison, setRaison] = useState('');
  const [bilan, setBilan] = useState(null);
  const [loadingBilan, setLoadingBilan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (modalShow) {
      setDate(initialDate || localToday());
      setVendeurId('');
      setEspece(''); setCheque(''); setBanque(''); setRaison('');
      setBilan(null); setError('');
    }
  }, [modalShow, initialDate]);

  useEffect(() => {
    if (!modalShow || !vendeurId || !date) {
      setBilan(null);
      return;
    }
    let active = true;
    const controller = new AbortController();
    setLoadingBilan(true);
    setBilan(null);
    setError('');
    axios.post(
      `${apiUrl}versements/bilan/${userData.id}/${userData.apitoken}/${vendeurId}/${date}`,
      {},
      { signal: controller.signal, timeout: 20000 }
    ).then(({ data }) => {
      if (active) setBilan(data);
    }).catch(err => {
      if (active && err.code !== 'ERR_CANCELED') {
        setError(err.response?.data?.detail || 'Impossible de récupérer le chiffre d’affaires.');
      }
    }).finally(() => { if (active) setLoadingBilan(false); });
    return () => { active = false; controller.abort(); };
  }, [modalShow, vendeurId, date, apiUrl, userData.id, userData.apitoken]);

  const e = parseMoney(espece);
  const c = parseMoney(cheque);
  const b = parseMoney(banque);
  const amountsValid = [e, c, b].every(n => Number.isFinite(n) && n >= 0 && Math.round(n * 100) === n * 100);
  const totalVerse = Math.round((e + c + b) * 100) / 100;
  const ca = Number(bilan?.ca || 0);
  const creditsEncaisses = Number(bilan?.credits_encaisses || 0);
  const caHorsCredits = Math.round((ca - creditsEncaisses) * 100) / 100;
  const manquesRecuperes = Number(bilan?.manques_recuperes || 0);
  const totalAttendu = Number(bilan?.total_attendu ?? ca + manquesRecuperes);
  const ecart = Math.round((totalAttendu - totalVerse) * 100) / 100;
  const manque = ecart > 0;
  const canSave = Boolean(vendeurId && date && bilan && !bilan.versement_existant && amountsValid && (!manque || raison.trim()) && !loadingBilan && !saving);

  const close = () => { if (!saving) dispatch(showmodal(false)); };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        vendeur_id: vendeurId, date,
        espece: e, cheque: c, banque: b,
        raison_manque: raison.trim()
      };
      const { data } = await axios.post(
        `${apiUrl}new/versement/${userData.id}/${userData.apitoken}`,
        payload, { timeout: 20000 }
      );
      if (data.status !== 'ok') throw new Error('Réponse inattendue du serveur');
      dispatch(addOne({ _id: data.id, date: data.date, ...data.versement }));
      dispatch(ajout(true));
      dispatch(showmodal(false));
      if (onSaved) onSaved(date);
      try {
        await imprimerBonVersement(apiUrl, userData.id, userData.apitoken, data.id);
      } catch (printError) {
        window.alert(`Versement enregistré, mais impression impossible : ${printError.message}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Erreur lors de l’enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={modalShow} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Nouveau versement</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Date de vente" type="date" value={date}
            onChange={ev => setDate(ev.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
          <FormControl fullWidth required>
            <InputLabel id="versement-vendeur-label">Vendeur</InputLabel>
            <Select labelId="versement-vendeur-label" label="Vendeur" value={vendeurId}
              onChange={ev => setVendeurId(ev.target.value)}>
              {listVendeurs.map(v => <MenuItem key={v._id} value={v._id}>{v.nom}</MenuItem>)}
            </Select>
          </FormControl>
          {loadingBilan && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Calcul du chiffre d’affaires…</Typography></Stack>}
          {bilan && <>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="body2">Chiffre d’affaires</Typography>
                  <Typography fontWeight={600}>{money(caHorsCredits)} DH</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="body2">Anciens crédits encaissés</Typography>
                  <Typography fontWeight={600}>{money(creditsEncaisses)} DH</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="body2">Anciens manques récupérés</Typography>
                  <Typography fontWeight={600}>{money(manquesRecuperes)} DH</Typography>
                </Stack>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>Total attendu</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">{money(totalAttendu)} DH</Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
            {bilan.versement_existant && <Alert severity="warning">Un versement existe déjà pour ce vendeur et cette date.</Alert>}
          </>}
          <Grid container spacing={2}>
            {[
              ['Montant en espèces', espece, setEspece],
              ['Montant en chèques', cheque, setCheque],
              ['Montant versé à la banque', banque, setBanque]
            ].map(([label, value, setter]) => (
              <Grid item xs={12} sm={6} key={label}>
                <TextField label={label} type="number" value={value} onChange={ev => setter(ev.target.value)}
                  inputProps={{ min: 0, step: '0.01' }} fullWidth />
              </Grid>
            ))}
          </Grid>
          {!amountsValid && <Alert severity="error">Les montants doivent être positifs, avec deux décimales maximum.</Alert>}
          {bilan && amountsValid && <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2">Total remis : {money(totalVerse)} DH</Typography>
            <Typography variant="h6" color={manque ? 'error.main' : ecart < 0 ? 'warning.main' : 'success.main'}>
              {manque ? 'Manque' : ecart < 0 ? 'Excédent' : 'Caisse équilibrée'} : {money(Math.abs(ecart))} DH
            </Typography>
          </Box>}
          {bilan && manque && <TextField label="Raison du manque" value={raison}
            onChange={ev => setRaison(ev.target.value)} multiline minRows={2} fullWidth required
            helperText="Obligatoire en cas de manque" />}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={saving}>Annuler</Button>
        <Button variant="contained" color="success" onClick={save} disabled={!canSave}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
